import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ROLE_FEATURES } from './role-features.map';
import { AppFeature } from './features.registry';
import { BUSINESS_FEATURES_WITH_HIERARCHY } from './role-features-hierarchy.map';

@Injectable({
    providedIn: 'root'
})
export class AccessControlService {

    /**
     * Holds all allowed feature IDs
     */
    private allowedFeatures = new Set<AppFeature>();

    /**
     * Emits when ACL is initialized
     */
    private userAclinitializedSubject = new BehaviorSubject<boolean>(false);
    userAclInitializedSubject$ = this.userAclinitializedSubject.asObservable();

    /**
    * Emits when Navigation Structure is initialized
    */
    private userNavInitializedSubject = new BehaviorSubject<boolean>(false);
    userNavInitializedSubject$ = this.userNavInitializedSubject.asObservable();

    constructor() { }

    /**
     * Initialize ACL after login or session restore
     * 
     * @param role - immutable role name from DB
     */
    initializeUserAcl(role: string): void {

        this.allowedFeatures.clear();

        // Role Features
        const roleFeatures =
            ROLE_FEATURES[role] || [];

        roleFeatures.forEach(feature =>
            this.allowedFeatures.add(feature)
        );

        this.userAclinitializedSubject.next(true);
    }

    /**
     * Initialize Navigation Menu after login or session restore
     * 
     * @param business_category - immutable business category name from DB
     */
    public initializeUserNavMenu(business_category: string): void {

        // Business Features With Hierarchy
        const businessFeaturesWithHierarchy = 
            BUSINESS_FEATURES_WITH_HIERARCHY[business_category] || [];   
        
        // normalize features allowed for logged in business
        const normalizedBusinessFeaturesWithHierarchy = businessFeaturesWithHierarchy
            .filter((feature) => this.allowedFeatures.has(feature.id))
            .map((feature) => this.normalizeFeature(feature));

        // Build navigation structure and persist in localStorage
        this.buildUserNavMenu(normalizedBusinessFeaturesWithHierarchy);
    }

    private normalizeFeature(feature: any) {
        return {
            id: feature.id,
            name: feature.name,
            hierarchy: feature.ui?.hierarchy || null,
            route: feature.ui?.route || null,
            icons: feature.ui?.icons || null,
        };
    }

    private buildUserNavMenu(features: any[]): void {
        const navStructure: any[] = [];
        const accessibleFeatures = features || [];

        accessibleFeatures.forEach((feature) => {
            const { hierarchy, icons, route, name } = feature;
            const { mainMenu, subMenu, level, order } = hierarchy || {};

            // ---- Helper to find or create a Main Menu ----
            const getOrCreateMainMenu = () => {
                let menu = navStructure.find(
                    (m) => m.type === 'menu' && m.name === mainMenu
                );
                if (!menu) {
                    menu = {
                        type: 'menu',
                        name: mainMenu,
                        children: [],
                        icon: icons?.menu || '',
                        order,
                    };
                    navStructure.push(menu);
                } else if (!menu.icon && (icons?.menu)) {
                    menu.icon = icons?.menu;
                    menu.order = order;
                }
                return menu;
            };

            // ---- Helper to find or create a Sub Menu ----
            const getOrCreateSubMenu = (menu: any) => {
                let submenu = menu.children.find(
                    (s) => s.type === 'submenu' && s.name === subMenu
                );
                if (!submenu) {
                    submenu = {
                        type: 'submenu',
                        name: subMenu,
                        children: [],
                        icon: icons?.group || '',
                        order,
                    };
                    menu.children.push(submenu);
                } else if (!submenu.icon && icons?.group) {
                    submenu.icon = icons?.group;
                    submenu.order = order;
                }
                return submenu;
            };

            // ---- Feature Data ----
            const featureData = {
                type: 'feature',
                name,
                routePath: route?.path,
                params: route?.params,
                queryParams: route?.queryParams,
                icon: icons?.main,
                order,
            };

            // ---- Add Feature Based on Hierarchy Level ----
            if (level === 0) {
                // Top-level direct feature
                navStructure.push(featureData);
            } else if (level === 1) {
                // Feature under a Main Menu
                const main = getOrCreateMainMenu();
                main.children.push({ ...featureData, icon: icons?.main });
            } else if (level === 2) {
                // Feature under a Sub Menu
                const main = getOrCreateMainMenu();
                const sub = getOrCreateSubMenu(main);
                sub.children.push(featureData);
            }
        });

        // Persist nav structure in localStorage
        localStorage.setItem('Nav_Structure', JSON.stringify(navStructure));

        this.userNavInitializedSubject.next(true);
    }

    /**
     * Check if feature is allowed
     */
    canAccess(feature: AppFeature): boolean {
        return this.allowedFeatures.has(feature);
    }

    /**
     * Check multiple features (AND condition)
     */
    canAccessAll(features: AppFeature[]): boolean {
        return features.every(f =>
            this.allowedFeatures.has(f)
        );
    }

    /**
     * Check multiple features (OR condition)
     */
    canAccessAny(features: AppFeature[]): boolean {
        return features.some(f =>
            this.allowedFeatures.has(f)
        );
    }

    /**
     * Remove feature dynamically (optional advanced use)
     */
    revokeFeature(feature: AppFeature): void {
        this.allowedFeatures.delete(feature);
    }

    /**
     * Add feature dynamically (optional advanced use)
     */
    grantFeature(feature: AppFeature): void {
        this.allowedFeatures.add(feature);
    }

    /**
     * Reset ACL (on logout)
     */
    reset(): void {
        this.allowedFeatures.clear();
        this.userAclinitializedSubject.next(false);
        this.userNavInitializedSubject.next(false);
    }

    /**
     * Debug helper (optional)
     */
    getAllAllowedFeatures(): string[] {
        return Array.from(this.allowedFeatures);
    }

    /**
     * Debug helper (optional)
     */
    getAllowedNavigationFeatures(): string[] {
        return Array.from(JSON.parse(localStorage.getItem('Nav_Structure')));
    }
}